/**
 * engagementModel.ts — the pure judgements behind the deal drawer's Engagement
 * tab (2026-09-23), kept out of the component so they can be tested without a
 * DOM — the `journeyModel.ts` split, in miniature.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: engagement tab.
 *
 * WHAT LIVES HERE: the sequence-status vocabulary, the "Step N of M" line, the
 * next-due derivation, the day label a step wears, and the MERGE of automated
 * steps and manual touches into one newest-first timeline. WHAT DOES NOT: any
 * fetch, any role gate, any JSX — the panel owns those.
 */
import type { DealEngagement, EngagementEnrolment, EngagementStep, EngagementTouch } from '../api/types';

/** The chip words for the sequence status — full words, never the raw enum. */
export const SEQUENCE_STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  stopped: 'Stopped',
  completed: 'Completed',
  exhausted: 'Exhausted',
};

/**
 * Humanise a stop-reason token (`consent_withdrawn` → "consent withdrawn").
 * The token is PHI-free config vocabulary, so de-underscoring it is a display
 * transform of an ops token, not a claim — and an unknown token still reads.
 */
export function stopReasonLabel(reason: string | null): string | null {
  if (!reason) return null;
  return reason.replace(/_/g, ' ');
}

/**
 * A step is SETTLED once the sequence engine has ruled on it. `pending` is the
 * future; `sending` is a CLAIMED step whose pass is mid-flight RIGHT NOW (the
 * engine's claim-then-send window, usually seconds — but a tab opened during
 * the daily cron will see it) — neither belongs on the settled timeline, and
 * the enrolment card summarises both as what is coming.
 */
export function isSettledStep(step: EngagementStep): boolean {
  return step.status !== 'pending' && step.status !== 'sending';
}

/**
 * "Step N of M" — N is the count of SETTLED steps (the engine has dealt with
 * them, whatever the outcome), M is the server's `stepsTotal`, never a
 * hard-coded 8. N is clamped to M so a payload with more settled rows than its
 * own total cannot print "Step 9 of 8".
 */
export function stepProgressLine(enrolment: EngagementEnrolment): string {
  const settled = Math.min(enrolment.steps.filter(isSettledStep).length, enrolment.stepsTotal);
  return `Step ${settled} of ${enrolment.stepsTotal}`;
}

/** The next step still on the clock — earliest `dueAt` among the live ones (`sending` included: it is due NOW). */
export function nextDueStep(enrolment: EngagementEnrolment): EngagementStep | null {
  const pending = enrolment.steps.filter((s) => s.status === 'pending' || s.status === 'sending');
  if (pending.length === 0) return null;
  return [...pending].sort((a, b) => (a.dueAt < b.dueAt ? -1 : 1))[0];
}

/**
 * The day label an automated step wears — "Day 5 email".
 *
 * FROM `templateKey` (the pinned scope): the sequence's keys are `day_<n>`
 * today, and the day the key stops carrying its day this falls back to the
 * step's OWN `dayOffset` — a named field on the same row — rather than parsing
 * anything else. Nothing clinical rides either value; this is an email
 * template's display name.
 */
export function stepDayLabel(step: Pick<EngagementStep, 'templateKey' | 'dayOffset'>): string {
  const match = /^day[_-]?(\d+)$/i.exec(step.templateKey);
  return `Day ${match ? match[1] : String(step.dayOffset)} email`;
}

/**
 * The timeline's status words for a settled step. An UNKNOWN status renders
 * its raw token (the `KIND_LABEL` Record idiom) — a claim-free degradation for
 * a status this build has not heard of.
 */
export const STEP_STATUS_LABEL: Record<string, string> = {
  sent: 'Sent',
  failed: 'Failed',
  skipped: 'Skipped',
  abandoned: 'Abandoned',
};

/** One merged timeline row — a settled automated step, or a manual touch. */
export type EngagementTimelineRow =
  | { kind: 'step'; id: string; at: string; step: EngagementStep }
  | { kind: 'touch'; id: string; at: string; touch: EngagementTouch };

/**
 * MERGE the automated steps and the manual touches into ONE timeline, newest
 * first. Steps sit at `settledAt` (falling back to `dueAt` for a settled step
 * an older server left unstamped — the row still has to sort somewhere), and
 * the sort is stable on id so two rows sharing an instant render the same way
 * every time.
 */
export function engagementTimeline(engagement: DealEngagement): EngagementTimelineRow[] {
  const rows: EngagementTimelineRow[] = [];
  for (const step of engagement.enrolment?.steps ?? []) {
    if (!isSettledStep(step)) continue;
    rows.push({ kind: 'step', id: `step-${step.templateKey}`, at: step.settledAt ?? step.dueAt, step });
  }
  for (const touch of engagement.touches) {
    rows.push({ kind: 'touch', id: `touch-${touch.id}`, at: touch.at, touch });
  }
  return rows.sort((a, b) => (a.at === b.at ? a.id.localeCompare(b.id) : a.at < b.at ? 1 : -1));
}

/**
 * WHICH ROWS CARRY THEIR OWN "Copy for WhatsApp" — every SENT item: an
 * automated step whose status is `sent`, and every `content_sent` touch. A
 * failed/skipped/abandoned step never offers a copy of an email that did not
 * go; a `whatsapp_logged` touch is already the record of a WhatsApp send.
 * ROLE is not this function's question — the panel withholds the button from a
 * CLINICIAN, and the server refuses them anyway (R41).
 */
export function rowCarriesCopy(row: EngagementTimelineRow): boolean {
  if (row.kind === 'step') return row.step.status === 'sent';
  return row.touch.kind === 'content_sent';
}

/**
 * The label a row's copy payload is announced under ("Copied — message and
 * link for …") and the log prompt is titled with.
 */
export function rowLabel(row: EngagementTimelineRow): string {
  if (row.kind === 'step') return stepDayLabel(row.step);
  if (row.touch.title) return row.touch.title;
  return row.touch.kind === 'content_sent' ? 'Content sent' : 'WhatsApp message logged';
}

/**
 * The contentId a row's copy/log pair rides — a `content_sent` touch carries
 * its content, an automated step carries none (the plain unlock message).
 */
export function rowContentId(row: EngagementTimelineRow): string | null {
  return row.kind === 'touch' ? row.touch.contentId : null;
}
